import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { PersonaType } from '@constants/global.enum';
import { TableConfig } from '@app/shared/components/table/table.interfaces';
import { ActionConfig } from '@app/shared/components/table/table-action-configs';
import {
  CONTRACTOR_TIMESHEET_COLUMNS,
  TIMESHEET_TABLE_CONFIG,
} from '../contractor-flow-table-configuration';
import { ContractorFlowService } from '../contractor-flow.service';
import { AuthService } from '@core/auth.service';
import { SearchGroupItem } from '@app/shared/interfaces/common.model';
import { VendorService } from '@app/shared/services/vendor.service';
import { ToasterService } from '@app/shared/services/toaster.service';
import { TIMESHEET_CONSTANTS } from '@app/pages/vendor-rep/timesheet/timesheet.constants';
import { DataService } from '@app/shared/services/data.service';
import { TimesheetReview } from '@app/shared/interfaces/vendor-management-model';
import { Months } from '@constants/constants';
import { ContractorLeaveComponent } from '../contractor-leave/contractor-leave.component';

@Component({
  selector: 'app-contractor-timesheet',
  templateUrl: './contractor-timesheet.component.html',
  styleUrl: './contractor-timesheet.component.scss',
})
export class ContractorTimesheetComponent implements OnInit {
  public readonly TIMESHEET_COLUMNS = CONTRACTOR_TIMESHEET_COLUMNS;
  TIMESHEET_CONSTANTS = TIMESHEET_CONSTANTS;
  showSubmitConfirmation: boolean = false;
  pendingSubmitData: any = null;
  timesheetData: any[] = [];
  filteredTimesheetData: any[] = [];
  tableConfig: TableConfig = TIMESHEET_TABLE_CONFIG;
  customActions: ActionConfig[] = TIMESHEET_TABLE_CONFIG.actions || [];
  maxTableHeight: string = '420px';

  static instance: ContractorTimesheetComponent;
  gridOptions: any = {
    context: {
      componentParent: this,
    },
  };

  calendarOpened = false;
  componentName = PersonaType.ContractorTimesheet;
  selectedContractor = {
    employeeId: '',
    employeeName: '',
    contractorName: '',
    jobTitle: '',
    location: '',
    vendorId: null as number | null,
  };
  selectedTimesheetMonthData: any | null = null;
  year: number = new Date().getFullYear();
  month: number = new Date().getMonth() + 1;

  searchValues: SearchGroupItem = {
    label: 'Timesheet',
    placeholder: 'Search by Month',
  };

  constructor(
    private contractorFlowService: ContractorFlowService,
    private authService: AuthService,
    private cdRef: ChangeDetectorRef,
    private vendorService: VendorService,
    private toasterService: ToasterService,
    private dataService: DataService,
  ) {}

  async ngOnInit(): Promise<void> {
    ContractorTimesheetComponent.instance = this;
    
    const profile = await this.authService.getEmployeeDetails();
    const employeeId = profile?.employeeId || '';
    this.selectedContractor = {
      employeeId,
      employeeName: profile?.employeeName || '',
      contractorName: profile?.employeeName || '',
      jobTitle: profile?.jobTitle || '',
      location: profile?.city || '',
      vendorId: profile?.vendorId || null,
    };
    this.loadContractorTimesheet();
  }

  public reloadTimesheetData(): void {
    this.loadContractorTimesheet();
  }

  private loadContractorTimesheet(): void {
    const employeeId = this.selectedContractor.employeeId;
    this.contractorFlowService.getContractorTimesheetApi(employeeId).subscribe({
      next: (resp) => {
        const rows = (resp?.data || []).map((item: any) => ({
          month: item?.monthName ?? '',
          workingHrs: `${item?.workingHours ?? 0} Hrs`,
          billableHrs: `${item?.billableHours ?? 0} Hrs`,
          leavesAvailed: `${item?.leavesAvailed ?? 0} Days`,
          status: item?.status ?? 'Pending Submission',
          monthNumber: parseInt(item?.monthNumber, 10),
          year: item?.year,
          timesheetMonthSummary: {
            monthNumber: parseInt(item?.monthNumber, 10),
            monthName: item?.monthName ?? '',
            year: item?.year,
            workingHours: item?.workingHours ?? 0,
            billableHours: item?.billableHours ?? 0,
            status: item?.status ?? 'Pending Submission',
            leavesAvailed: item?.leavesAvailed ?? 0,
            paidLeaves: item?.paidLeaves ?? 0,
            unpaidLeaves: item?.unpaidLeaves ?? 0,
            quarterlyLimit: item?.quarterlyLimit ?? 0,
            quarterlyLeavesAvailed: item?.quarterlyLeavesAvailed ?? 0,
            paidLeaveDates: item?.paidLeaveDates ?? [],
            unpaidLeaveDates: item?.unpaidLeaveDates ?? [],
            publicHolidayDates: item?.publicHolidayDates ?? [],
            halfPaidLeaveDates: item?.halfPaidLeaveDates ?? [],
            halfUnpaidLeaveDates: item?.halfUnpaidLeaveDates ?? [],
            billableDates: item?.billableDates ?? [],
          },
        }));
        this.timesheetData = rows;
        this.filteredTimesheetData = [...rows];
      },
      error: () => {
        this.timesheetData = [];
        this.filteredTimesheetData = [];
      },
    });
  }

  onSearch(term: string): void {
    const q = (term || '').trim().toLowerCase();
    if (!q) {
      this.filteredTimesheetData = [...this.timesheetData];
      return;
    }
    this.filteredTimesheetData = this.timesheetData.filter((row) =>
      String(row.month).toLowerCase().includes(q),
    );
  }

  onClear(): void {
    this.filteredTimesheetData = [...this.timesheetData];
  }

  openCalendar(event?: MouseEvent, rowData?: any): void {
    const derivedMonth =
      rowData?.monthNumber ?? this.mapMonthNameToNumber(rowData?.month);
    if (derivedMonth) this.month = derivedMonth;
    if (rowData?.year) this.year = rowData.year;
    this.selectedTimesheetMonthData = rowData?.timesheetMonthSummary ?? null;
    this.calendarPosition = { top: '50%', left: '50%' };
    this.calendarOpened = true;
    this.cdRef.detectChanges();
  }

  closeCalendar(): void {
    this.calendarOpened = false;
    this.cdRef.detectChanges();
  }

  onCellClicked(event: any): void {
    if (event?.colDef?.field === 'calendarView') {
      this.openCalendar(event?.event as MouseEvent, event?.data);
    }
  }

  private mapMonthNameToNumber(monthName?: string): number | null {
    if (!monthName) return null;
    const idx = Months.findIndex(
      (m) => m.toLowerCase() === String(monthName).toLowerCase(),
    );
    return idx >= 0 ? idx + 1 : null;
  }

  onSubmitTimesheet(data: any): void {
    this.pendingSubmitData = data;
    this.showSubmitConfirmation = true;
    this.cdRef.detectChanges();
  }

  onConfirmSubmit(): void {
    this.showSubmitConfirmation = false;
    const data = this.pendingSubmitData;
    this.pendingSubmitData = null;
    const userProfile = this.dataService?.userProfile();
    const loggedInUserName = [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ');
    const payload: TimesheetReview = {
      employeeId: userProfile?.employeeId ? [userProfile.employeeId] : [],
      reviewedBy: loggedInUserName,
      status: TIMESHEET_CONSTANTS.STATUS.PENDING_APPROVAL,
      month: data?.monthNumber,
      year: data?.year,
    };

    this.vendorService.updateTimesheetStatusForVendorRep(payload).subscribe({
      next: (response: any) => {
        this.toasterService.showSuccess(response?.reason, response?.status);
        this.loadContractorTimesheet();
        // Reload contractor leaves data after timesheet submission
        if (ContractorLeaveComponent.instance) {
          ContractorLeaveComponent.instance.reloadLeavesData();
        }
      },
      error: () => {
        this.toasterService.showError(
          TIMESHEET_CONSTANTS.MESSAGES.SUBMISSION_FAILED,
          TIMESHEET_CONSTANTS.TITLES.SUBMISSION_ERROR,
        );
      },
    });
  }

  onCancelSubmit(): void {
    this.showSubmitConfirmation = false;
    this.pendingSubmitData = null;
  }

  calendarPosition: { top: string; left: string } = { top: '50%', left: '50%' };
}